import {useEffect, useRef, useState} from 'react';
import {
  Root as ToastRoot,
  ToastClose,
  ToastDescription,
  ToastProvider,
  ToastViewport,
} from '@radix-ui/react-toast';

import {Icon} from '#component/base/Icon';
import {Text} from '#component/base/Text';
import {sprinkles} from '#style/sprinkles';

import {ToastProps} from './Toast.types';

import * as css from './Toast.css';

/**
 * Toasts are succinct messages that is displayed temporarily, and appear
 * in a container placed overtop of the rest of the viewport content, but do
 * not prevent interaction with the underlying content.
 *
 * This component is build on top of the @radix-ui `Toast` component, and accepts
 * all of their `Root` component props. For more details, see:
 * - https://www.radix-ui.com/docs/primitives/components/toast
 *
 * Note that `Toast`s are managed by a `ToastProvider` context values, which are
 * shared with a `Toaster` component that manages the visibility of all Toasts
 * within the app. For most usecases, you should use the `useToast` hook, and
 * not this component directly.
 */
export const Toast = ({children, duration = 5000, ...props}: ToastProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isBlurred, setIsBlurred] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const viewportRef = useRef<HTMLOListElement>(null);
  const isPaused = isHovered || isBlurred || isFocused;

  const handleFocusIn = () => {
    if (!isHovered) {
      setIsFocused(true);
    }
  };

  const handleFocusOut = () => {
    if (!isHovered) {
      setIsFocused(false);
    }
  };

  const handlePointerMove = () => {
    setIsHovered(true);
  };

  const handlePointerLeave = () => {
    setIsHovered(false);
  };

  const handleWindowFocus = () => {
    setIsBlurred(false);
  };

  const handleWindowBlur = () => {
    setIsBlurred(true);
  };

  useEffect(() => {
    // NOTE: An upcoming release to the Radix `Toast` component will provide
    // `onPause` and `onResume` props, which will allow us to remove this code,
    // which is currently used only to keep the visual progress bars in sync
    // with Radix's own internally-managed pause state, which isnt't currently
    // exposed.
    const viewportEl = viewportRef.current;

    if (viewportEl) {
      viewportEl.addEventListener('focusin', handleFocusIn);
      viewportEl.addEventListener('focusout', handleFocusOut);
      viewportEl.addEventListener('pointermove', handlePointerMove);
      viewportEl.addEventListener('pointerleave', handlePointerLeave);
      window.addEventListener('blur', handleWindowBlur);
      window.addEventListener('focus', handleWindowFocus);

      return () => {
        viewportEl.removeEventListener('focusin', handleFocusIn);
        viewportEl.removeEventListener('focusout', handleFocusOut);
        viewportEl.removeEventListener('pointermove', handlePointerMove);
        viewportEl.removeEventListener('pointerleave', handlePointerLeave);
        window.removeEventListener('blur', handleWindowBlur);
        window.removeEventListener('focus', handleWindowFocus);
      };
    }
  });

  return (
    <ToastProvider duration={duration}>
      <ToastViewport ref={viewportRef}>
        <ToastRoot className={css.root} {...props}>
          <div className={css.contentWrapper}>
            <Text as="div" className={css.content} color="burgundy">
              <Icon
                name="WarningCircle"
                aria-hidden
                className={sprinkles({shrink: 0})}
                size="small"
                weight="regular"
                color="currentColor"
              />

              <ToastDescription>{children}</ToastDescription>
            </Text>

            <div className={css.divider} />
          </div>

          <Text as={ToastClose} aria-label="Close" className={css.closeButton} color="doveGray">
            <Icon name="X" aria-hidden size="xSmall" weight="bold" />
          </Text>

          <div
            className={css.progress}
            style={{
              animationPlayState: isPaused ? 'paused' : 'initial',
              animationDuration: `${duration}ms`,
            }}
          />
        </ToastRoot>
      </ToastViewport>
    </ToastProvider>
  );
};
